import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DefaultModal from 'components/Utils/DefaultModal';
import PatientSearchField from 'components/Patient/PatientSearchField';
import StaffSearchField from 'components/Staff/StaffSearchField';
import CalendarUtils from 'services/CalendarUtils';
import ContentEditable from 'components/Utils/ContentEditable';
import Icon from 'components/Utils/NewIcon';
import List from '@material-ui/core/List';
import IconButton from '@material-ui/core/IconButton';
import StaffUtils, { PERMISSIONS } from 'services/StaffUtils';
import StringUtils from 'services/StringUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import usePersistedState from 'hooks/usePersistedState';
import DefaultTextField from 'components/Utils/DefaultTextField';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import AddDocumentAttachmentModal from 'components/Modals/AddDocumentAttachmentModal';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DocumentDetailsModal from 'components/HealthRecords/Documents/DocumentDetailsModal';
import { DefaultSwitch } from 'services/UIUtils';
import CheckIcon, { FloatTopCPPIcon } from 'components/Tasks/Utils/CheckIcon';
import ResponsiveColumns from 'components/ResponsiveColumns';
import { IsAdmin } from 'careand-redux/selectors';
import moment from 'moment-timezone';
import PatientBadge from 'components/Patient/PatientBadge';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import useAssigneeAway from 'hooks/useAssigneeAway';
import AIUtils from 'services/AIUtils';
import MiscUtils from 'services/MiscUtils';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';
import { runSavedPromptTyped } from 'thunks/runSavedPromptTyped';

const DEFAULT_ARRAY = [];

const TaskDetailsView = (props) => {
  const { value, onClose, deleteDocument } = props;
  const { comments = DEFAULT_ARRAY, documents = DEFAULT_ARRAY } = value;

  let className = 'add-task details-panel';
  let showComments = !!value.id || comments.length > 0;
  let showDocuments = documents.length > 0;

  if (showDocuments) className += ' show-documents';
  if (showComments) className += ' show-comments';

  return (
    <>
      {onClose !== undefined && (
        <div className="top-bar">
          <div className="left-wrapper">
            <Icon src="arrows-alt" width="20px" height="20px" className="handle" />
            {value.id && (
              <Icon
                width="25px"
                height="25px"
                alt="Mark Task Unread"
                showTooltip={true}
                src="envelope"
                loading={props.submitting}
                onClick={props.onMarkUnread}
              />
            )}
            {value.id && <ConvertToNoteButton task={value} />}
          </div>
          <Icon
            alt="Close"
            src="xmark"
            showTooltip={true}
            className="close-button"
            onClick={onClose}
            width="25px"
            height="25px"
          />
        </div>
      )}
      <div className={className}>
        <ResponsiveColumns
          columns={[
            { name: 'Edit', element: <MainContent {...props} /> },
            showComments
              ? {
                  name: 'Comments',
                  element: (
                    <Comments
                      value={value}
                      saveComment={props.saveComment}
                      deleteComment={props.deleteComment}
                      submitting={props.submitting}
                      ownId={props.ownId}
                      setMessagesEnd={props.setMessagesEnd}
                    />
                  ),
                }
              : undefined,
            showDocuments
              ? {
                  name: 'Documents',
                  element: (
                    <Documents
                      documents={documents}
                      showDocuments={showDocuments}
                      deleteDocument={deleteDocument}
                    />
                  ),
                }
              : undefined,
          ]}
        />
      </div>
    </>
  );
};

const ConvertToNoteButton = ({ task }) => {
  const isAdmin = useAppSelector(IsAdmin);
  const dispatch = useAppDispatch();
  const allDocuments = useAppSelector((state) => state.documents.all);
  const authentication = useAppSelector((state) => state.authentication);
  const [noteText, setNoteText] = useState(null);
  const [creatingNote, setCreatingNote] = useState(false);
  const [hideNote, setHideNote] = useState(true);

  const isSavingNote = useIsLoading(api.HealthRecords.addNotesText);

  const originalDocuments = useMemo(() => {
    return (task.documents || [])
      .map((id) => allDocuments.find((document) => document.id === id))
      .filter((doc) => !!doc);
  }, [allDocuments, task.documents]);

  if (isAdmin || !task.patient_id) return null;

  async function createNoteInCPP() {
    setCreatingNote(true);

    let taskContent = {
      ...task,
      date: CalendarUtils.format(task.date),
      todays_date: CalendarUtils.format(moment().unix()),
    };
    if (originalDocuments?.length > 0) {
      taskContent.documents = originalDocuments.map((document) => {
        return `${document.title} - ${document.document_type} (${CalendarUtils.format(
          document.date,
        )})`;
      });
    }

    const result = await runSavedPromptTyped(
      'Task to Note',
      { task: JSON.stringify(taskContent, null, 2) },
      { patientId: task.patient_id },
    );
    if (result) setNoteText(result);
    setCreatingNote(false);
  }

  async function saveNote() {
    let appointmentNote = {
      patient_id: task.patient_id,
      item_type: 'text',
      author_id: authentication.user.id,
      author_role: authentication.role,
      value: {
        value: noteText,
        date: moment().unix(),
        hide: hideNote,
      },
    };
    let result = await dispatch(api.HealthRecords.addNotesText({ appointmentNote }), {
      title: 'Save Note',
    });
    if (result) setNoteText(null);
  }

  return (
    <>
      {noteText !== null && (
        <DefaultModal
          className="task-note-modal fit-modal"
          wrap={true}
          padding={true}
          content={
            <>
              <PatientBadge id={task.patient_id} />
              <div className="row">
                <TextareaAutosize
                  label="Note"
                  style={{ width: '600px', maxWidth: '90vw' }}
                  value={noteText}
                  disabled={isSavingNote}
                  onChange={(event) => {
                    let text = event.target.value;
                    setNoteText(text);
                  }}
                />
              </div>
              <div className="row mb-4">
                <DefaultSwitch
                  name="hide"
                  padded={false}
                  onChange={(event, isInputChecked) => {
                    setHideNote(isInputChecked);
                  }}
                  label="Hide Note From Patient"
                  checked={hideNote}
                />
              </div>
              <div className="row">
                <DefaultButton
                  style={{ width: '100%' }}
                  className="blue"
                  onClick={saveNote}
                  name="Save Encounter Note"
                  disabled={!noteText}
                  loading={isSavingNote}
                />
              </div>
            </>
          }
          isOpen={true}
          onRequestClose={() => setNoteText(null)}
          title="Create Encounter Note"
        />
      )}
      <Icon
        width="25px"
        height="25px"
        alt="Create Encounter Note in CPP"
        showTooltip={true}
        src="file-import"
        loading={creatingNote}
        onClick={createNoteInCPP}
      />
    </>
  );
};

export const Comments = ({
  value,
  saveComment,
  deleteComment,
  submitting,
  ownId,
  setMessagesEnd,
  commentsKey = 'comments',
  singleButton = false,
  title = 'Comments',
}) => {
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const permissions = useAppSelector((state) => state.authentication.permissions);

  const [currentComment, setComment] = usePersistedState(
    'clear.' + value.id + '-comment',
    '',
    true,
  );

  function handleTypedCommentChange(event) {
    setComment(event.target.value);
  }

  async function iconClickedNoNotification() {
    return saveCommentWrapper(currentComment, false);
  }

  async function iconClicked() {
    return saveCommentWrapper(currentComment);
  }

  function onKeyDown(event, currentMesssage = currentComment) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      saveCommentWrapper(currentMesssage);
    }
  }

  async function saveCommentWrapper(comment, notification = true) {
    if (!!comment && !submitting) {
      setComment('');
      let result = await saveComment(comment, notification);
      if (!result) setComment(comment);
    }
  }

  let comments = [...(value[commentsKey] || [])];

  async function removeComment(comment) {
    if (!!comment && !submitting) {
      await deleteComment(comment);
    }
  }

  return (
    <div className="comments full-height">
      <div className="small-title">{title}</div>
      <div className="comment-list scroll-list">
        {comments.length === 0 && <span className="no-comments">No comments found</span>}
        {comments.map((comment) => {
          const user = admins[comment.author_id] || physicians[comment.author_id];

          const name = StaffUtils.getName(user) || comment.author_id;
          const isCarey = AIUtils.getAssistantName() === name;

          let className = 'comment';
          const isOwn = ownId === comment.author_id;
          const allowDelete = isOwn || permissions.includes(PERMISSIONS.super_admin);
          if (isOwn) className += ' own';
          if (allowDelete || isCarey) className += ' has-hover';

          const accent = StringUtils.colorFromString(comment.author_id);
          const initials = StringUtils.getInitials(name).slice(0, 2) || '?';
          const photo = user?.photo;

          return (
            <div className={className} key={comment.date}>
              <div className="comment-header">
                <div className="avatar" style={photo ? undefined : { background: accent }}>
                  {photo ? <img src={photo} alt={name} /> : initials}
                </div>
                <div className="meta">
                  <div className="author">{StaffUtils.getName(user) || comment.author_id}</div>
                  <div className="date">
                    {CalendarUtils.format(comment.date, 'DD/MM/YYYY h:mm A')}
                  </div>
                </div>
                <div className="actions">
                  {allowDelete && (
                    <div className="remove">
                      <ConfirmationIcon
                        src="trash"
                        height="20px"
                        width="20px"
                        alt="Delete comment"
                        showTooltip={true}
                        style={{ margin: 0 }}
                        confirmText="Are you sure you want to delete this comment?"
                        onClick={() => removeComment(comment)}
                      />
                    </div>
                  )}
                  {isCarey && (
                    <div className="copy">
                      <Icon
                        src="copy"
                        height="20px"
                        width="20px"
                        alt="Copy to clipboard"
                        showTooltip={true}
                        style={{ margin: 0 }}
                        onClick={() => {
                          navigator.clipboard.writeText(comment.text);
                          MiscUtils.showToastrInfo(
                            {
                              title: 'Comment copied',
                              message: "Carey's comment has been copied to your clipboard.",
                            },
                            {
                              closeOnToastrClick: false,
                              timeout: 1000,
                              position: 'bottom-center',
                            },
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="text">{comment?.text}</div>
            </div>
          );
        })}
        <div
          style={{ float: 'left', clear: 'both' }}
          ref={setMessagesEnd ? (el) => setMessagesEnd(el) : undefined}
        />
      </div>
      <div className="comment-box">
        <div className="input-bar" onKeyDown={onKeyDown} tabIndex={0}>
          <ContentEditable
            key="send_message-bar"
            name="send_message-bar"
            disabled={true}
            onChange={handleTypedCommentChange}
            text={currentComment}
            placeholder="Add a comment here..."
          />
          <div className="icons">
            {singleButton ? (
              <>
                <Icon
                  id="send_message"
                  src="message"
                  className="icon-submit"
                  alt="Submit Comment"
                  showTooltip={true}
                  height="21px"
                  width="21px"
                  onClick={iconClickedNoNotification}
                />
              </>
            ) : (
              <>
                <Icon
                  id="send_message-silent-button"
                  src="bell-slash"
                  className="icon-submit"
                  alt="Send without notification"
                  showTooltip={true}
                  height="21px"
                  width="21px"
                  onClick={iconClickedNoNotification}
                />
                <Icon
                  id="send_message-button"
                  src="bell"
                  alt="Send and notify"
                  showTooltip={true}
                  className="icon-submit"
                  height="17px"
                  width="17px"
                  onClick={iconClicked}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Documents = ({ documents = [], showDocuments, deleteDocument }) => {
  const dispatch = useAppDispatch();
  const allDocuments = useAppSelector((state) => state.documents.all);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState();

  const originalDocuments = useMemo(() => {
    return documents
      .map((id) => {
        return allDocuments.find((document) => document.id === id);
      })
      .filter((doc) => !!doc);
  }, [allDocuments, documents]);

  const missingIds = useMemo(() => {
    let fullIds = originalDocuments.map(({ id }) => id);
    return documents.filter((id) => !fullIds.includes(id));
  }, [originalDocuments, documents]);

  useEffect(() => {
    if (missingIds.length > 0) {
      setLoading(true);
      dispatch(
        api.Documents.getMultipleDocuments({ multipleValuesPayload: { values: missingIds } }),
      ).then((result) => {
        setLoading(false);
      });
    }
  }, [dispatch, missingIds]);

  return (
    showDocuments && (
      <div className="documents full-height scroll-list">
        {!!selected && (
          <DocumentDetailsModal
            document={selected}
            isOpen={!!selected}
            title="Document Details"
            onClose={() => setSelected(undefined)}
            onEdit={() => setSelected(undefined)}
          />
        )}
        <div className="documents-content row">
          <span className="small-title">Documents</span>
          <ProgressMessage show={loading} />
          <List>
            {originalDocuments.map((document) => (
              <SelectableListItem
                key={document.id}
                className="list-item"
                padded={false}
                onClick={() => setSelected(document)}
                item={
                  <DocumentBadge item={document}>
                    <IconButton
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        deleteDocument(document);
                      }}
                      className="delete"
                    >
                      <Icon
                        width="20px"
                        src="trash"
                        className="red"
                        showTooltip={true}
                        tooltipPlace="left"
                      />
                    </IconButton>
                  </DocumentBadge>
                }
              />
            ))}
          </List>
        </div>
      </div>
    )
  );
};

const MainContent = ({
  value,
  updateField,
  submitting,
  patient,
  staff,
  showPatient,
  onSelectPatient,
  onSubmit,
  onSelectStaff,
  onClaim,
  onSave,
  onArchive,
  isPrivateTask,
}) => {
  const [documentModal, setDocumentModal] = useState(false);
  const author = useAppSelector(
    (state) => state.physicians.byId[value.author_id] || state.admins.byId[value.author_id],
  );
  const { away: assigneeAway, reason: awayReason } = useAssigneeAway(value.assigned_user_id);

  return (
    <div className="task-content">
      <form onSubmit={onSubmit}>
        <div className="icons-wrapper">
          <FloatTopCPPIcon
            float={value.pinned}
            onClick={() => updateField('pinned', !value.pinned)}
          />
          <CheckIcon
            task={value}
            onClick={() => updateField('status', !value.status)}
            width="30px"
            height="30px"
          />
          <DefaultTextField
            id="title-field"
            name="title"
            placeholder="Title"
            className="title-field"
            disabled={submitting}
            value={value.title}
            style={{ width: 'auto', flexGrow: 1 }}
            onChange={(event) => updateField('title', event.target.value)}
            autoFocus={true}
          />
        </div>
        <div className="row">
          <span className="small-title">Details</span>
          <TextareaAutosize
            cacheMeasurements
            name="details"
            maxRows={4}
            value={value.details}
            disabled={submitting}
            onChange={(event) => updateField('details', event.target.value)}
          />
        </div>
        <div className="row">
          <DefaultDatePicker
            label="Relevant by Date"
            id="date-picker"
            value={value.date}
            onChange={(event, date) => updateField('date', date)}
            style={{
              width: '48.5%',
              display: 'inline-block',
              marginRight: '3%',
            }}
            disabled={submitting}
          />
          <StaffTimePicker
            label="Relevant By Time"
            id="time-picker"
            value={value.time || value.date}
            style={{ width: '48.5%' }}
            onChange={(event, date) => updateField('time', date)}
            disabled={submitting}
          />
        </div>
        <div className="row">
          <DefaultDatePicker
            key={value.due_date}
            label="Due by"
            id="date-picker-due"
            value={value.due_date}
            onChange={(event, date) => updateField('due_date', date)}
            style={{
              width: '46.5%',
              display: 'inline-block',
              marginRight: '3%',
            }}
            disabled={submitting}
          />
          {!!value.due_date && !submitting && (
            <div className="clear-wrapper" onClick={() => updateField('due_date', null)}>
              <Icon src="xmark" />
            </div>
          )}
          {value.patient_id && (
            <div className="add-document-wrapper">
              <DefaultButton
                name="Add Documents"
                icon="file-plus"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setDocumentModal(true);
                }}
                className="white"
                style={{ width: '155px' }}
              />
            </div>
          )}
          {isPrivateTask && (
            <div className="row">
              <DefaultSwitch
                onChange={(event, isInputChecked) =>
                  updateField('dont_notify_documents', !isInputChecked)
                }
                label="Notifications for tracked documents"
                padded={false}
                checked={!value.dont_notify_documents}
              />
            </div>
          )}
          {documentModal && (
            <DefaultModal
              key="add-document-attachment-modal"
              className="large-modal add-document-attachment-modal"
              wrap={true}
              padding={false}
              title="Add Task Attachments"
              content={
                <AddDocumentAttachmentModal
                  patient={patient}
                  useIds={true}
                  selected={value.documents}
                  onSubmit={(documents) => {
                    updateField('documents', documents);
                    setDocumentModal(false);
                    if (value.id) onSave({ ...value, documents }, true);
                  }}
                />
              }
              isOpen={documentModal}
              onRequestClose={() => setDocumentModal(false)}
            />
          )}
        </div>
        {author && (
          <div className="row">
            <div className="small-title mt-1">
              <span>Created by</span>
              <span style={{ marginLeft: '5px', fontWeight: 'normal' }}>
                {StaffUtils.getName(author)}
              </span>
            </div>
          </div>
        )}
        {showPatient && (
          <div className="row patient-search-wrapper">
            <PatientSearchField hideTitle={true} onSelect={onSelectPatient} patient={patient} />
          </div>
        )}
        <div className="row">
          <StaffSearchField
            hideTitle={true}
            onSelect={onSelectStaff}
            staff={staff}
            hideInactive={true}
          />
          {assigneeAway && (
            <div className="ooo-warning">
              <Icon src="triangle-exclamation" width="14px" alt="Out of office" />
              {/* The assignee can be from another clinic, and so absent from the
                  local staff store — still warn, just without a name. */}
              <span>
                {(staff && StaffUtils.getFirstName(staff)) || 'Assignee'} is out of office today.
              </span>
            </div>
          )}
          {!staff && (
            <IconButton
              onClick={onClaim}
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Icon showTooltip={true} alt="Claim Task" src="user-plus" width="25px" />
            </IconButton>
          )}
        </div>
        <div className="save-button row full-width">
          {value.id && (
            <DefaultButton
              name="Archive"
              icon="trash"
              style={{ width: '120px', float: 'left' }}
              onClick={() => onArchive(value)}
              disabled={!onArchive}
              className="red"
            />
          )}
          <DefaultButton
            name="Save Changes"
            className="blue"
            style={{ width: '45%' }}
            onClick={() => onSave(value)}
            disabled={submitting}
          />
        </div>
      </form>
    </div>
  );
};

export default TaskDetailsView;
